import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'
import { createPinia } from 'pinia'
import axios from 'axios'
import '@fontsource/lato/300.css'
import '@fontsource/lato/400.css'
import '@fontsource/lato/700.css'
import router from './router'
import App from './App.vue'
import './style.css'
import { useAuthStore } from './stores/auth'
import { cleanupOldTokens } from './utils/cleanupOldTokens'
import { themeStore } from './stores/theme'
import { repairPasswordResetUrlForHashRouting } from './utils/passwordResetUrl'

// cookie always send with request
axios.defaults.withCredentials = true

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Apply saved / system theme before first paint (login and all routes).
themeStore.initializeTheme()

// SECURITY: Clean up any old tokens from localStorage (from before cookie migration)
cleanupOldTokens()

// Suppress browser extension errors in console (harmless, but noisy)
if (import.meta.env.DEV) {
  const originalError = console.error
  console.error = (...args: any[]) => {
    const message = args[0]?.toString() || ''
    // Filter out browser extension errors
    if (
      message.includes('runtime.lastError') ||
      message.includes('message port closed') ||
      message.includes('Extension context invalidated')
    ) {
      return // Suppress these errors
    }
    originalError.apply(console, args)
  }
}

// Initialize auth store from localStorage
const authStore = useAuthStore()
authStore.initializeFromStorage()

// If localStorage says we're authenticated but cookies were cleared/expired,
// background badge-count requests will spam 401s. Validate the session once on boot.
if (authStore.isLoggedIn) {
  // Teachers authenticate via OTP and are not present in the `users` table, so `/api/users/me`
  // can 401 even when the teacher session is valid. Use token-status instead (works for all roles).
  fetch('/api/sessions/token-status', { credentials: 'include' })
    .then((r) => {
      // token-status returns 200 with JSON always; still guard against network/edge failures
      if (!r.ok) authStore.clearAuthState()
      return r.json().catch(() => null)
    })
    .then((body) => {
      const expired = body?.data?.tokenExpiration?.isExpired === true
      if (expired) authStore.clearAuthState()
    })
    .catch(() => {
      authStore.clearAuthState()
    })
}

// Full DSN only — e.g. https://xxx@o123.ingest.us.sentry.io/456 (Settings → Client Keys in Sentry)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim()
if (sentryDsn?.startsWith('https://') && sentryDsn.includes('@')) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    integrations: [],
    tracesSampleRate: 0.1,
    environment: import.meta.env.MODE
  })
}

void router.isReady().then(() => {
  if (repairPasswordResetUrlForHashRouting()) {
    return
  }
  app.mount('#app')
})
